import React, { useState } from 'react';
import { useAppStore } from '@/lib/store';
import { copyToClipboard } from '@/utils/format';

export default function SettingsView() {
  const { settingsTab, setSettingsTab, copiedId, setCopiedId, userName, userEmail, logout, logoutCount, incrementLogout } = useAppStore();
  const [localLogoutCount, setLocalLogoutCount] = useState(0);

  const handleLogout = () => {
    const next = localLogoutCount + 1;
    setLocalLogoutCount(next);
    if (next >= 2) {
      logout();
    }
  };

  const copy = (text, id) => {
    copyToClipboard(text);
    setCopiedId(id);
  };

  const colorSections = [
    { title: 'Backgrounds & Surfaces', items: [
      { name: 'Main Background', hex: '#232426' },
      { name: 'Lighter Panel', hex: '#323335' },
      { name: 'Hover State', hex: '#3e3f42' },
    ]},
    { title: 'Borders & Dividers', items: [
      { name: 'Divider / Soft Border', hex: '#515153' },
      { name: 'Subtle Border', hex: '#3e3f42' },
    ]},
    { title: 'Brand & Accents', items: [
      { name: 'Primary Brand', hex: '#6498CC' },
      { name: 'Accent Notice', hex: '#F6661E' },
      { name: 'Tab Active BG', hex: '#784A15' },
      { name: 'Tab Active Text', hex: '#ED8B38' },
    ]},
  ];

  const typographySections = [
    { title: 'Typography Colors', items: [
      { name: 'Text Main', hex: '#d5d6d9' },
      { name: 'Text Dim', hex: '#8f9196' },
      { name: 'Text Dimmer', hex: '#b3b4b7' },
      { name: 'Pure White', hex: '#ffffff' },
    ]},
  ];

  const iconSections = [
    { title: 'Navigation', items: [
      { name: 'Menu', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M3 12h18M3 6h18M3 18h18"/></svg>' },
      { name: 'Chevron Down', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 9l-7 7-7-7"/></svg>' },
      { name: 'Search', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>' },
      { name: 'Settings', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/></svg>' },
      { name: 'Plus', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>' },
      { name: 'Folder', svg: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M10 4H4C2.9 4 2 4.9 2 6v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/></svg>' },
    ]},
  ];

  return (
    <div className="flex-1 flex flex-col p-4 md:p-6 overflow-y-auto">
      <h1 className="text-white font-bold text-[18px] md:text-xl mb-6">Settings & Styling System</h1>

      <div className="flex items-center gap-5 mb-8">
        <div className="flex bg-transparent border border-[#515153] rounded-[4px] overflow-hidden text-[13.5px] font-medium h-[30px]">
          {['profile', 'colors', 'icons', 'typography'].map(t => (
            <button key={t} onClick={() => setSettingsTab(t)} className={`px-5 h-full transition-colors flex items-center justify-center border-r border-[#515153] last:border-r-0 capitalize ${settingsTab === t ? 'bg-[#784A15] text-[#ED8B38]' : 'text-[#d5d6d9] hover:bg-[#323335]'}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {copiedId && (
        <div className="fixed top-16 right-6 bg-[#6498CC] text-white px-4 py-2 rounded shadow-lg text-[13px] font-medium z-50 animate-pulse">Copied!</div>
      )}

      {settingsTab === 'profile' && (
        <div className="max-w-lg space-y-6">
          {/* Avatar + name */}
          <div className="bg-[#323335] border border-[#515153] rounded-xl p-6 flex items-center gap-5">
            <div className="w-16 h-16 rounded-full bg-[#6498CC] flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
              {(userName || 'G').charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="text-white font-bold text-[18px]">{userName || 'Gnanesh'}</div>
              <div className="text-[#8f9196] text-[13px] mt-0.5">{userEmail || 'gnanies@amazon.com'}</div>
              <div className="mt-1.5 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#71e184]" />
                <span className="text-[#71e184] text-[12px] font-medium">Active</span>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="bg-[#323335] border border-[#515153] rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-[#515153]">
              <h3 className="text-white font-semibold text-[13px]">Account Details</h3>
            </div>
            {[
              { label: 'Full Name', value: userName || 'Gnanesh' },
              { label: 'Email', value: userEmail || 'gnanies@amazon.com' },
              { label: 'User ID', value: 'user_default' },
              { label: 'Workspace', value: 'Palinos Personal' },
              { label: 'Plan', value: 'Personal (Unlimited)' },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between px-5 py-3 border-b border-[#3e3f42] last:border-b-0">
                <span className="text-[#8f9196] text-[13px]">{row.label}</span>
                <span className="text-[#d5d6d9] text-[13px] font-medium">{row.value}</span>
              </div>
            ))}
          </div>

          {/* Logout */}
          <div className="bg-[#323335] border border-[#515153] rounded-xl p-5">
            <h3 className="text-white font-semibold text-[13px] mb-3">Sign Out</h3>
            <p className="text-[#8f9196] text-[12px] mb-4">
              You will be returned to the login page. Click the button twice to confirm.
            </p>
            <button
              onClick={handleLogout}
              className={`px-5 py-2 rounded-md font-semibold text-[13px] transition-all border ${
                localLogoutCount === 0
                  ? 'border-[#515153] text-[#8f9196] hover:border-[#e17171] hover:text-[#e17171] hover:bg-[#2d1414]'
                  : 'border-[#e17171] text-[#e17171] bg-[#2d1414] animate-pulse'
              }`}
            >
              {localLogoutCount === 0 ? 'Logout' : `${localLogoutCount}/2 — Click again to confirm`}
            </button>
          </div>
        </div>
      )}

      {settingsTab === 'colors' && (
        <div className="space-y-8">
          {colorSections.map((section, idx) => (
            <div key={idx}>
              <h2 className="text-[#8f9196] text-[14px] font-semibold mb-4 uppercase tracking-wider">{section.title}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {section.items.map(color => (
                  <div key={color.hex} onClick={() => copy(color.hex, color.hex)} className="bg-[#323335] p-4 rounded-md border border-[#515153] flex flex-col gap-3 cursor-pointer hover:border-[#6498CC] hover:shadow-[0_0_8px_rgba(100,152,204,0.3)] transition-all group" title="Click to copy">
                    <div className="w-full h-12 md:h-16 rounded border border-[#515153]" style={{ backgroundColor: color.hex }} />
                    <div className="flex justify-between items-end">
                      <div>
                        <div className="text-white text-[12px] md:text-[13px] font-bold">{color.name}</div>
                        <div className="text-[#8f9196] text-[11px] md:text-[12px] font-mono mt-1">{color.hex}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {settingsTab === 'typography' && (
        <div className="space-y-8">
          {typographySections.map((section, idx) => (
            <div key={idx}>
              <h2 className="text-[#8f9196] text-[14px] font-semibold mb-4 uppercase tracking-wider">{section.title}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {section.items.map(color => (
                  <div key={color.hex} onClick={() => copy(color.hex, color.hex)} className="bg-[#323335] p-4 rounded-md border border-[#515153] flex flex-col gap-3 cursor-pointer hover:border-[#6498CC] transition-all group" title="Click to copy">
                    <div className="w-full h-12 md:h-16 rounded border border-[#515153] flex items-center justify-center bg-[#232426]">
                      <span style={{ color: color.hex }} className="font-bold text-[20px]">Aa</span>
                    </div>
                    <div>
                      <div className="text-white text-[13px] font-bold">{color.name}</div>
                      <div className="text-[#8f9196] text-[12px] font-mono mt-1">{color.hex}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {settingsTab === 'icons' && (
        <div className="space-y-8">
          {iconSections.map((section, idx) => (
            <div key={idx}>
              <h2 className="text-[#8f9196] text-[14px] font-semibold mb-4 uppercase tracking-wider">{section.title}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {section.items.map((icon, i) => (
                  <div key={i} onClick={() => copy(icon.svg, icon.name)} className="bg-[#323335] p-4 rounded-md border border-[#515153] flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-[#6498CC] hover:bg-[#3e3f42] transition-all group" title="Click to copy SVG">
                    <div className="w-8 h-8 text-[#d5d6d9] group-hover:text-white flex items-center justify-center" dangerouslySetInnerHTML={{ __html: icon.svg }} />
                    <span className="text-[12px] text-[#8f9196] group-hover:text-white text-center">{icon.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
